import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { RootState } from '~/store';
import { ValidationContext } from '~/components/hoc/withValidation';
import { Center, Loader, Stack } from '@qred/components-library';
import KlarnaKosmaBankAccountList from './KlarnaKosmaBankAccountList';
import ManualAccountForm from './ManualAccountForm';
import BankConnectionTroubleBanner from './BankConnectionTroubleBanner';
import PayoutAccountLabel from './PayoutAccountLabel';
import { useBankInformationFooter } from '../BankInformationFooterContext';
import { isEmptyObject } from '~/helpers/utils';
import { useGetBankAccountsQuery } from '~/store/apis/endpoints/onboardingApi/onboarding.api';
import BankConnection from '~/components/shared/BankConnection/BankConnection';
import OpenBankingAccountList from './OpenBankingAccountList';
import { updateForm } from '~/store/slices/loanOffer.slice';
import { BankConnectionResult } from '~/enums';
import useTranslate from '~/hooks/useTranslate';
import { getOpenBankingAccountNumber } from '~/helpers/shared.helper';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { IOpenBankingAccount } from '~/interfaces/shared';
import useManualAccountForm from '../../../hooks/useManualAccountForm';

const SelectPayoutAccount = () => {
  const validationContext = useContext(ValidationContext);
  const [bankConnectionError, setBankConnectionError] = useState(false);
  // The verified account selected before "Add a different account", so going back selects it again
  const accountBeforeAddingNewRef = useRef<IOpenBankingAccount | null>(null);
  const translate = useTranslate();
  const {
    loanOffer: { overview, form },
  } = useSelector((state: RootState) => state);
  const {
    showAddNewAccount,
    setShowAddNewAccount,
    setIsBankSearchShown,
    setHasNoSearchResults,
    startAuthorizationContainer,
  } = useBankInformationFooter();
  const {
    data: openBankingAccounts,
    isFetching: isFetchingBankAccounts,
    isError: fetchingBankAccountsFailed,
    refetch,
  } = useGetBankAccountsQuery(
    { clientId: overview.clientId },
    {
      skip: !overview.clientId,
    }
  );
  const canUseBankConnection = !fetchingBankAccountsFailed;
  const { isManualAccountFormShown } = useManualAccountForm();
  const dispatch = useAppDispatch();

  const showManualAccountInputField =
    form.accountNumber !== null || fetchingBankAccountsFailed;

  const hasBankAccountsViaKlarnaKosma = Boolean(overview.bankAccounts?.length);
  const hasBankAccountsViaOpenBanking = Boolean(openBankingAccounts?.length);
  const hasBankAccounts =
    hasBankAccountsViaKlarnaKosma || hasBankAccountsViaOpenBanking;

  useEffect(() => {
    if (
      isEmptyObject(form.account) &&
      !form.accountNumber &&
      !form.openBankingAccount
    ) {
      validationContext.addPropertyToValidationErrors('MissingAccount');
    }

    return () => {
      validationContext.removePropertyFromValidationErrors('MissingAccount');
    };
  }, [form.account, form.accountNumber, form.openBankingAccount]);

  const isBankSearchShown =
    !isFetchingBankAccounts &&
    canUseBankConnection &&
    showAddNewAccount &&
    !showManualAccountInputField;

  useEffect(() => {
    setIsBankSearchShown(isBankSearchShown);
  }, [isBankSearchShown]);

  // Every way to manual entry, including the footer button, should close the trouble banner
  useEffect(() => {
    if (form.accountNumber !== null) {
      setBankConnectionError(false);
    }
  }, [form.accountNumber]);

  useEffect(
    () => () => {
      setIsBankSearchShown(false);
      setShowAddNewAccount(false);
    },
    []
  );

  useEffect(() => {
    if (
      !isFetchingBankAccounts &&
      (!hasBankAccounts || form.accountNumber !== null)
    ) {
      setShowAddNewAccount(true);
    }
  }, [isFetchingBankAccounts, hasBankAccounts, form.accountNumber]);

  useEffect(() => {
    if (showAddNewAccount || !accountBeforeAddingNewRef.current) {
      return;
    }

    dispatch(
      updateForm({
        account: {},
        accountNumber: null,
        openBankingAccount: accountBeforeAddingNewRef.current,
      })
    );
    accountBeforeAddingNewRef.current = null;
  }, [showAddNewAccount]);

  const handleAccountSelected = (value: string) => {
    setShowAddNewAccount(false);
    const selectedAccount = openBankingAccounts?.find(
      (account) => getOpenBankingAccountNumber(account) === value
    );
    if (!selectedAccount) return;

    dispatch(
      updateForm({
        account: {},
        accountNumber: null,
        openBankingAccount: selectedAccount,
      })
    );
  };

  const handleAddNewAccount = () => {
    dispatch(
      pushToGtmLoanAction({
        actionName: 'add_different_account_pressed',
      })
    );
    accountBeforeAddingNewRef.current = form.openBankingAccount;
    dispatch(
      updateForm({
        account: {},
        accountNumber: null,
        openBankingAccount: null,
      })
    );
    setBankConnectionError(false);
    setShowAddNewAccount(true);
  };

  const onBankConnectionResult = (result: BankConnectionResult) => {
    dispatch(
      pushToGtmLoanAction({
        actionName: `bank_connection_${result}`,
      })
    );
    if (
      result === BankConnectionResult.Error ||
      result === BankConnectionResult.Cancelled
    ) {
      setBankConnectionError(true);
    }

    if (result === BankConnectionResult.Success) {
      // The customer picks from the list with the new account instead
      accountBeforeAddingNewRef.current = null;
      setShowAddNewAccount(false);
      refetch();
    }
  };

  const onAddManuallyAfterBankConnectionError = () => {
    dispatch(
      pushToGtmLoanAction({
        actionName: 'bank_connection_trouble_add_manually_pressed',
      })
    );
    dispatch(updateForm({ accountNumber: '' }));
  };

  const onBankNotListedSelected = () => {
    dispatch(
      pushToGtmLoanAction({
        actionName: 'bank_not_listed_selected',
      })
    );
    dispatch(updateForm({ accountNumber: '' }));
  };

  if (isFetchingBankAccounts) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  return (
    <Stack spacing={'xl'}>
      {canUseBankConnection ? (
        <>
          {hasBankAccountsViaOpenBanking ? (
            !showAddNewAccount && (
              <OpenBankingAccountList
                handleAccountSelected={handleAccountSelected}
                handleAddNewAccount={handleAddNewAccount}
              />
            )
          ) : (
            <>
              {hasBankAccountsViaKlarnaKosma && (
                <KlarnaKosmaBankAccountList
                  setShowAddNewAccount={setShowAddNewAccount}
                  showAddNewAccount={showAddNewAccount}
                />
              )}
            </>
          )}

          {showAddNewAccount && (
            <>
              {isManualAccountFormShown ? (
                <ManualAccountForm />
              ) : (
                <Stack spacing={'sm'}>
                  {bankConnectionError && (
                    <BankConnectionTroubleBanner
                      onAddManually={onAddManuallyAfterBankConnectionError}
                      onClose={() => setBankConnectionError(false)}
                    />
                  )}
                  <PayoutAccountLabel />
                  <BankConnection
                    clientId={overview.clientId}
                    setError={setBankConnectionError}
                    allowCancel={false}
                    onResult={onBankConnectionResult}
                    hasError={bankConnectionError}
                    showPsuTypeLabel
                    onManualEntrySelected={onBankNotListedSelected}
                    searchHint={translate('BankConnection.SearchHint')}
                    searchHelp={{
                      label: translate('BankConnection.SearchHelpLabel'),
                      text: translate('BankConnection.SearchHelpText', {
                        manualEntryOptionNoResults: translate(
                          'BankConnection.ManualEntryOptionNoResults'
                        ),
                        manualEntryOption: translate(
                          'BankConnection.ManualEntryOption'
                        ),
                      }),
                    }}
                    onNoSearchResultsChange={setHasNoSearchResults}
                    startAuthorizationContainer={startAuthorizationContainer}
                    showErrorMessages={false}
                  />
                </Stack>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {hasBankAccountsViaKlarnaKosma && (
            <KlarnaKosmaBankAccountList
              setShowAddNewAccount={setShowAddNewAccount}
              showAddNewAccount={showAddNewAccount}
            />
          )}

          {isManualAccountFormShown && <ManualAccountForm />}
        </>
      )}
    </Stack>
  );
};

export default SelectPayoutAccount;
